import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import type { AuditEntry } from '../types';

const ACTION_TAGS: Record<string, { label: string; cls: string }> = {
  create: { label: 'Created', cls: 'ok' },
  update: { label: 'Updated', cls: '' },
  delete: { label: 'Deleted', cls: 'danger' },
  restore: { label: 'Restored', cls: 'warn' },
  upload: { label: 'Uploaded', cls: '' },
  escalate: { label: 'Escalated', cls: 'escalate' },
};

const ENTITY_LABELS: Record<string, string> = {
  lease: 'Lease',
  tenant: 'Tenant',
  property: 'Property',
  document: 'Document',
  reconciliation: 'Reconciliation',
  user: 'User',
};

const camelToTitle = (s: string): string =>
  s.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

const formatVal = (v: unknown): string => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

const formatTime = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
};

const LIMIT = 50;

export const AuditPage = () => {
  const [rows, setRows] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.audit(page, LIMIT, filter || undefined);
      setRows(data.rows);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => { void load(); }, [load]);

  const totalPages = Math.ceil(total / LIMIT);

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div className="page">
      <h1>Audit Trail</h1>
      <div className="toolbar">
        <select value={filter} onChange={(e) => { setFilter(e.target.value); setPage(1); }}>
          <option value="">All types</option>
          <option value="lease">Leases</option>
          <option value="tenant">Tenants</option>
          <option value="property">Properties</option>
          <option value="document">Documents</option>
          <option value="reconciliation">Reconciliations</option>
          <option value="user">Users</option>
        </select>
        <span className="spacer" />
        <span className="muted">{total} entries</span>
      </div>

      <div className="table-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th style={{ width: 150 }}>Time</th>
              <th style={{ width: 120 }}>User</th>
              <th style={{ width: 90 }}>Action</th>
              <th style={{ width: 90 }}>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24 }}>Loading...</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: 'var(--muted)' }}>No audit entries yet</td></tr>
            )}
            {rows.map((entry) => {
              const tag = ACTION_TAGS[entry.action] ?? { label: entry.action, cls: '' };
              const isExpanded = expanded.has(entry.id);
              const hasChanges = entry.changes && Object.keys(entry.changes).length > 0;
              return (
                <tr key={entry.id} onClick={() => hasChanges && toggle(entry.id)} style={{ cursor: hasChanges ? 'pointer' : 'default' }}>
                  <td className="muted">{formatTime(entry.createdAt)}</td>
                  <td>{entry.userName}</td>
                  <td><span className={`tag ${tag.cls}`}>{tag.label}</span></td>
                  <td>{ENTITY_LABELS[entry.entityType] ?? entry.entityType}</td>
                  <td>
                    <div>{entry.label}</div>
                    {hasChanges && !isExpanded && (
                      <span className="muted" style={{ fontSize: 11 }}>
                        {Object.keys(entry.changes!).length} field(s) changed — click to expand
                      </span>
                    )}
                    {hasChanges && isExpanded && (
                      <div className="audit-changes">
                        {Object.entries(entry.changes!).map(([key, { old: oldVal, new: newVal }]) => (
                          <div key={key} className="audit-change-row">
                            <span className="audit-field">{camelToTitle(key)}</span>
                            <span className="audit-old">{formatVal(oldVal)}</span>
                            <span className="audit-arrow">&rarr;</span>
                            <span className="audit-new">{formatVal(newVal)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
          <span className="muted">Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
};
